import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementBlock as _createElementBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-33f16ba0"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "live-keyword-page" }

import { computed, onMounted, reactive, ref } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Delete, Plus } from '@element-plus/icons-vue'
import dayjs from 'dayjs'
import {
  CrudDialog,
  CrudForm,
  CrudSearch,
  CrudTable
} from '@/components/crud-v2'
import { usePagedCrud } from '@/hooks/crud/usePagedCrud'
import { usePermission } from '@/hooks/usePermission'
import type { CrudSelectOption } from '@/types/crud-config'
import {
  createLiveKeywordFormItems,
  liveKeywordSearchItems,
  createLiveKeywordTableColumns
} from './live-keyword.config'
import {
  createLiveKeyword,
  deleteLiveKeyword,
  fetchLiveKeywordEventOptions,
  fetchLiveKeywordPage
} from './live-keyword.service'
import type {
  LiveKeywordFilters,
  LiveKeywordForm,
  LiveKeywordRecord
} from './live-keyword.service'

type FormRef = InstanceType<typeof CrudForm>


export default /*#__PURE__*/_defineComponent({
  __name: 'LiveKeyword',
  setup(__props) {

const canList = usePermission('liveKeyword', 'list')
const canCreate = usePermission('liveKeyword', 'create')
const canDelete = usePermission('liveKeyword', 'delete')

function createFilters(): LiveKeywordFilters {
  return {
    addTime: [
      dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss')
    ]
  }
}

function createEmptyForm(): LiveKeywordForm {
  return { keyword: '', eventsId: '', addTime: [] }
}

const {
  filters,
  pageNo,
  pageSize,
  records,
  total,
  error,
  loading,
  load,
  search,
  reset,
  changePage,
  changePageSize,
  refresh,
  refreshAfterDelete
} = usePagedCrud({ createFilters, fetchPage: fetchLiveKeywordPage })

const eventOptions = ref<CrudSelectOption[]>([])
const optionsLoading = ref(false)
const deletingId = ref<LiveKeywordRecord['id']>()
const dialogVisible = ref(false)
const formRef = ref<FormRef>()
const form = reactive<LiveKeywordForm>(createEmptyForm())

const formItems = computed(() =>
  createLiveKeywordFormItems(eventOptions.value, optionsLoading.value)
)
const tableColumns = createLiveKeywordTableColumns(canDelete)
const errorMessage = computed(() =>
  error.value instanceof Error ? error.value.message : ''
)

const rules = {
  keyword: { required: true, message: '关键词是必传的', trigger: 'blur' },
  eventsId: { required: true, message: '直播间是必传的', trigger: 'change' },
  addTime: { required: true, message: '时间范围是必传的', trigger: 'change' }
}

function runQuery(action: () => Promise<boolean>) {
  return canList ? action() : Promise.resolve(false)
}

async function loadEventOptions() {
  optionsLoading.value = true
  try {
    const events = await fetchLiveKeywordEventOptions()
    eventOptions.value = events.map((item) => ({
      label: item.eventName,
      value: item.rowId
    }))
  } catch (cause) {
    ElMessage.error(
      cause instanceof Error ? cause.message : '直播间选项加载失败'
    )
  } finally {
    optionsLoading.value = false
  }
}

function openCreateDialog() {
  Object.assign(form, createEmptyForm())
  dialogVisible.value = true
}

async function submitForm() {
  if (!(await formRef.value!.validate())) return false
  await createLiveKeyword({ ...form })
  return true
}

async function handleCreateSuccess() {
  ElMessage.success('新增成功')
  await refresh()
}

async function handleDelete(row: LiveKeywordRecord) {
  try {
    await ElMessageBox.confirm('确认删除此项目吗？', '提示', {
      confirmButtonText: '确认',
      cancelButtonText: '取消',
      type: 'warning'
    })
  } catch {
    return
  }

  deletingId.value = row.id
  try {
    await deleteLiveKeyword(row.id)
    ElMessage.success('删除成功')
    await refreshAfterDelete()
  } catch (cause) {
    ElMessage.error(cause instanceof Error ? cause.message : '删除失败')
  } finally {
    deletingId.value = undefined
  }
}

onMounted(() => {
  loadEventOptions()
  runQuery(load)
})

return (_ctx: any,_cache: any) => {
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_time_picker = _resolveComponent("el-time-picker")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(CrudSearch), {
      model: _unref(filters),
      items: _unref(liveKeywordSearchItems),
      loading: _unref(loading),
      onSearch: _cache[0] || (_cache[0] = ($event: any) => (runQuery(_unref(search)))),
      onReset: _cache[1] || (_cache[1] = ($event: any) => (runQuery(_unref(reset))))
    }, null, 8, ["model", "items", "loading"]),
    _createVNode(_unref(CrudTable), {
      rows: _unref(records),
      "page-no": _unref(pageNo),
      "page-size": _unref(pageSize),
      total: _unref(total),
      loading: _unref(loading),
      "error-message": errorMessage.value,
      columns: _unref(tableColumns),
      onPageChange: _cache[2] || (_cache[2] = ($event: any) => (runQuery(() => _unref(changePage)($event)))),
      onPageSizeChange: _cache[3] || (_cache[3] = ($event: any) => (runQuery(() => _unref(changePageSize)($event)))),
      onRetry: _cache[4] || (_cache[4] = ($event: any) => (runQuery(_unref(refresh))))
    }, {
      title: _withCtx(() => [
        _createTextVNode("统计列表")
      ]),
      actions: _withCtx(() => [
        (_unref(canCreate))
          ? (_openBlock(), _createBlock(_component_el_button, {
              key: 0,
              type: "primary",
              onClick: openCreateDialog
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(Plus), {
                  class: "live-keyword-page__button-icon",
                  "aria-hidden": "true"
                }),
                _createTextVNode(" 新增 ")
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      rowActions: _withCtx(({ row }) => [
        (_unref(canDelete))
          ? (_openBlock(), _createBlock(_component_el_button, {
              key: 0,
              type: "text",
              loading: deletingId.value === row.id,
              disabled: deletingId.value !== undefined,
              onClick: _withModifiers(($event: any) => (handleDelete(row)), ["stop"])
            }, {
              default: _withCtx(() => [
                (deletingId.value !== row.id)
                  ? (_openBlock(), _createBlock(_unref(Delete), {
                      key: 0,
                      class: "live-keyword-page__button-icon",
                      "aria-hidden": "true"
                    }))
                  : _createCommentVNode("", true),
                _createTextVNode(" 删除 ")
              ]),
              _: 2
            }, 1032, ["loading", "disabled", "onClick"]))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["rows", "page-no", "page-size", "total", "loading", "error-message", "columns"]),
    _createVNode(_unref(CrudDialog), {
      modelValue: dialogVisible.value,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((dialogVisible).value = $event)),
      title: "新增关键词",
      submit: submitForm,
      onSuccess: handleCreateSuccess,
      onSubmitError: _cache[6] || (_cache[6] = ($event: any) => (
        _unref(ElMessage).error($event instanceof Error ? $event.message : '新增失败')
      ))
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(CrudForm), {
          ref_key: "formRef",
          ref: formRef,
          model: form,
          items: formItems.value,
          rules: rules
        }, {
          timeRange: _withCtx(({ model, item }) => [
            _createVNode(_component_el_time_picker, {
              modelValue: model[item.prop],
              "onUpdate:modelValue": ($event: any) => ((model[item.prop]) = $event),
              "is-range": "",
              class: "live-keyword-page__time-range",
              "range-separator": "-",
              "start-placeholder": "开始时间",
              "end-placeholder": "结束时间"
            }, null, 8, ["modelValue", "onUpdate:modelValue"])
          ]),
          _: 1
        }, 8, ["model", "items"])
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})